import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Download, Code, Zap, Users, Crown, ArrowRight, CreditCard, Calendar, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { MAINTENANCE_MODE } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchasePage = () => {
  const navigate = useNavigate();
  const [paymentType, setPaymentType] = useState('onetime');
  const [loading, setLoading] = useState(false);

  const licenses = [
    {
      name: 'Personal',
      icon: Code,
      price: 297,
      paymentPlan: '3 x $109',
      color: 'from-blue-500 to-cyan-600',
      popular: false,
      features: [
        'Complete CryptoPulse Pro platform',
        'Full source code access',
        'Use on 1 website',
        'AI prediction system (Gemini)',
        'Binance trading integration',
        'Stripe payment integration',
        'User authentication system',
        'Portfolio & alerts features',
        'Documentation included',
        '1 year of updates',
        'Email support',
        'Deployment guide'
      ]
    },
    {
      name: 'Business',
      icon: Zap,
      price: 697,
      paymentPlan: '3 x $249',
      color: 'from-emerald-500 to-green-600',
      popular: true,
      features: [
        'Everything in Personal',
        'Use on unlimited websites',
        'White-label ready',
        'Remove "Made with Emergent" badge',
        'Custom branding support',
        'Lifetime updates',
        'Priority email support',
        'Video tutorials',
        'Migration assistance',
        'Commercial use rights'
      ]
    },
    {
      name: 'Agency',
      icon: Users,
      price: 1997,
      paymentPlan: '3 x $699',
      color: 'from-purple-500 to-violet-600',
      popular: false,
      features: [
        'Everything in Business',
        'Resell to unlimited clients',
        'Agency/developer license',
        'Custom feature development',
        'White-label templates',
        'Client onboarding docs',
        'Priority support (24h response)',
        '1-on-1 onboarding call',
        'Developer API docs',
        'Slack community access'
      ]
    },
    {
      name: 'Enterprise',
      icon: Crown,
      price: 4997,
      paymentPlan: '3 x $1,749',
      color: 'from-amber-500 to-orange-600',
      popular: false,
      features: [
        'Everything in Agency',
        'Full platform customization',
        'Dedicated account manager',
        'Custom feature development',
        'Server deployment service',
        'Team training (up to 5 people)',
        '3 months dedicated support',
        'White-glove onboarding',
        'Custom integrations',
        'Priority feature requests'
      ]
    }
  ];

  const handlePurchase = async (licenseName, price) => {
    // Block purchases during maintenance
    if (MAINTENANCE_MODE) {
      toast.error('Purchases Unavailable', {
        description: 'Platform purchases are temporarily disabled during maintenance.'
      });
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/purchase/checkout`, {
        license_type: licenseName.toLowerCase(),
        payment_type: paymentType,
        price: paymentType === 'onetime' ? price : Math.ceil(price / 3)
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Maintenance Notice */}
      {MAINTENANCE_MODE && (
        <div className="fixed top-16 left-0 right-0 z-40 bg-amber-500/20 border-b border-amber-500/30 py-4 px-4">
          <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <p className="text-amber-400 font-medium">
              Purchases are temporarily unavailable during maintenance. Please check back later.
            </p>
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/50 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">CryptoPulse Pro</h1>
                <p className="text-xs text-gray-400">Purchase License</p>
              </div>
            </button>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-gray-300 hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Own the Complete
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600 mt-2">
                CryptoPulse Pro Platform
              </span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Get instant access to the full source code, documentation, and lifetime updates.
              Deploy to your own servers and start earning today.
            </p>

            {/* Payment Type Selector */}
            <Tabs value={paymentType} onValueChange={setPaymentType} className="w-full max-w-md mx-auto mb-12">
              <TabsList className="grid w-full grid-cols-2 bg-white/5">
                <TabsTrigger value="onetime" className="data-[state=active]:bg-emerald-500">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay Once
                </TabsTrigger>
                <TabsTrigger value="plan" className="data-[state=active]:bg-purple-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  3 Payments
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {licenses.map((license, index) => (
              <motion.div
                key={license.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                {license.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-emerald-500 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <Card className={`p-6 h-full flex flex-col ${
                  license.popular
                    ? 'bg-gradient-to-br from-emerald-500/10 to-purple-600/10 border-emerald-500/50 scale-105'
                    : 'bg-white/5 border-white/10'
                }`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${license.color} flex items-center justify-center mb-4`}>
                    <license.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-2">{license.name}</h3>
                  
                  <div className="mb-6">
                    {paymentType === 'onetime' ? (
                      <>
                        <span className="text-4xl font-bold">${license.price}</span>
                        <span className="text-gray-400 ml-2">one-time</span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">{license.paymentPlan}</span>
                        <span className="text-gray-400 ml-2 block text-sm">3 monthly payments</span>
                      </>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {license.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handlePurchase(license.name, license.price)}
                    disabled={loading || MAINTENANCE_MODE}
                    className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${
                      license.popular
                        ? 'bg-gradient-to-r from-emerald-500 to-purple-600 hover:from-emerald-600 hover:to-purple-700'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {MAINTENANCE_MODE ? 'Currently Unavailable' : loading ? 'Processing...' : 'Get Instant Access'}
                    {!MAINTENANCE_MODE && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-gradient-to-r from-emerald-500/10 to-purple-600/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            What You Get <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600">Instantly</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Code,
                title: 'Complete Source Code',
                description: 'Full React frontend, FastAPI backend, MongoDB integration. Everything you need to deploy.'
              },
              {
                icon: Download,
                title: 'Instant Download',
                description: 'Get immediate access to GitHub repository or zip download. Start building in minutes.'
              },
              {
                icon: Zap,
                title: 'Ready to Deploy',
                description: 'One-click deployment guides for AWS, DigitalOcean, Heroku, or your own servers.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400 text-sm">
          <p>© 2026 CryptoPulse Pro. All rights reserved.</p>
          <p className="mt-2">Secure payment processing by Stripe • Instant access upon purchase</p>
        </div>
      </footer>
    </div>
  );
};

export default PurchasePage;